'use client'

import {
  type CanvasAnimationMeta,
  type DeviceTier,
  useCanvasAnimation,
} from '@/hooks/use-canvas-animation'

interface GradientRibbonCanvasProps {
  className?: string
}

// ── Configuration ──────────────────────────────────────────────────────
// Two ribbons: upper (indigo/emerald cool) and lower (rose/amber warm).
// They enter from an off-canvas source on the left and fan out across
// the canvas. Each ribbon has an independent macro sway so they sometimes
// cross through each other, giving the visual a living, breathing feel.
const STROKE_COUNTS: Record<DeviceTier, { upper: number; lower: number }> = {
  desktop: { upper: 90, lower: 90 },
  tablet: { upper: 60, lower: 60 },
  mobile: { upper: 30, lower: 30 },
}
const STROKE_WIDTH = 0.9

// ── Color palette — resolved per frame from DOM class ─────────────────
function getColors() {
  const isDark =
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  return isDark
    ? {
        upperStart: [66, 56, 220] as const,
        upperMid: [124, 124, 255] as const,
        upperEnd: [124, 228, 196] as const,
        lowerStart: [244, 114, 182] as const,
        lowerMid: [251, 113, 133] as const,
        lowerEnd: [251, 191, 36] as const,
      }
    : {
        upperStart: [79, 70, 229] as const,
        upperMid: [91, 91, 245] as const,
        upperEnd: [52, 211, 153] as const,
        lowerStart: [244, 114, 182] as const,
        lowerMid: [244, 63, 94] as const,
        lowerEnd: [245, 158, 11] as const,
      }
}

// ── Core draw — called by the hook on each rAF tick ───────────────────
function drawRibbons(ctx: CanvasRenderingContext2D, meta: CanvasAnimationMeta, time: number): void {
  const { w, h, dpr, tier } = meta
  if (w === 0 || h === 0) return

  const { upper: upperCount, lower: lowerCount } = STROKE_COUNTS[tier]

  ctx.clearRect(0, 0, w * dpr, h * dpr)
  ctx.save()
  ctx.scale(dpr, dpr)

  const c = getColors()

  // ── Macro sway — slow, large-scale vertical wobble of each ribbon's
  //    center at the source (left) and spread (right). Out-of-phase
  //    sines cause the ribbons to occasionally cross.
  const upperSrcSway = Math.sin(time / 4800) * h * 0.14
  const lowerSrcSway = Math.sin(time / 5200 + 2.3) * h * 0.14
  const upperSpreadSway = Math.sin(time / 4200 + 0.6) * h * 0.22
  const lowerSpreadSway = Math.sin(time / 3800 + 2.7) * h * 0.22

  // Source sits off-canvas to the left — lifted slightly above center
  // so the visible left-edge entry sits higher than before.
  const sourceX = -w * 0.2
  const spreadX = w * 1.02

  // Upper source centred around 42% height (visibly above centre)
  const upperSourceY = h * 0.42 + upperSrcSway
  // Lower source centred around 58% height — close to upper, so they
  // read as "emerging from roughly the same left region" but still
  // distinguishable.
  const lowerSourceY = h * 0.58 + lowerSrcSway

  // Spread centres — these swing enough that the ribbons cross through
  // each other on long cycles.
  const upperSpreadCenter = h * 0.3 + upperSpreadSway
  const lowerSpreadCenter = h * 0.72 + lowerSpreadSway

  ctx.lineWidth = STROKE_WIDTH
  ctx.lineCap = 'round'

  // Upper ribbon (cool — indigo → emerald)
  for (let i = 0; i < upperCount; i++) {
    const t = i / (upperCount - 1)
    const phase = t * Math.PI * 2 + time / 2400
    const wobble1 = Math.sin(phase) * 6
    const wobble2 = Math.sin(phase * 1.7 + time / 1800) * (h * 0.08)
    const wobble3 = Math.cos(phase * 0.9 - time / 3200) * (h * 0.04)

    const startX = sourceX
    const startY = upperSourceY + (t - 0.5) * 12
    const endX = spreadX
    const endY = upperSpreadCenter + (t - 0.5) * (h * 0.42) + wobble2

    // Animated control points create a flowing S-curve that reshapes
    // over time
    const cp1x = w * (0.28 + Math.sin(time / 3600) * 0.04)
    const cp1y = upperSourceY - h * 0.04 + wobble1 + wobble3
    const cp2x = w * (0.68 + Math.cos(time / 4100) * 0.04)
    const cp2y = upperSpreadCenter + (t - 0.5) * (h * 0.3) + wobble1 + wobble3

    const grad = ctx.createLinearGradient(startX, startY, endX, endY)
    grad.addColorStop(0, `rgba(${c.upperStart.join(',')},0)`)
    grad.addColorStop(0.22, `rgba(${c.upperStart.join(',')},0.5)`)
    grad.addColorStop(0.55, `rgba(${c.upperMid.join(',')},0.65)`)
    grad.addColorStop(0.9, `rgba(${c.upperEnd.join(',')},0.22)`)
    grad.addColorStop(1, `rgba(${c.upperEnd.join(',')},0)`)

    ctx.beginPath()
    ctx.moveTo(startX, startY)
    ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, endX, endY)
    ctx.strokeStyle = grad
    ctx.stroke()
  }

  // Lower ribbon (warm — rose → amber)
  for (let i = 0; i < lowerCount; i++) {
    const t = i / (lowerCount - 1)
    const phase = t * Math.PI * 2 + time / 2200 + Math.PI / 2
    const wobble1 = Math.sin(phase) * 6
    const wobble2 = Math.sin(phase * 1.5 - time / 2000) * (h * 0.09)
    const wobble3 = Math.cos(phase * 1.1 + time / 3500) * (h * 0.05)

    const startX = sourceX
    const startY = lowerSourceY + (t - 0.5) * 12
    const endX = spreadX
    const endY = lowerSpreadCenter + (t - 0.5) * (h * 0.34) + wobble2

    const cp1x = w * (0.3 + Math.cos(time / 3400) * 0.04)
    const cp1y = lowerSourceY + h * 0.05 + wobble1 + wobble3
    const cp2x = w * (0.66 + Math.sin(time / 3900) * 0.04)
    const cp2y = lowerSpreadCenter - (t - 0.5) * (h * 0.22) + wobble1 + wobble3

    const grad = ctx.createLinearGradient(startX, startY, endX, endY)
    grad.addColorStop(0, `rgba(${c.lowerStart.join(',')},0)`)
    grad.addColorStop(0.22, `rgba(${c.lowerStart.join(',')},0.4)`)
    grad.addColorStop(0.55, `rgba(${c.lowerMid.join(',')},0.55)`)
    grad.addColorStop(0.9, `rgba(${c.lowerEnd.join(',')},0.2)`)
    grad.addColorStop(1, `rgba(${c.lowerEnd.join(',')},0)`)

    ctx.beginPath()
    ctx.moveTo(startX, startY)
    ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, endX, endY)
    ctx.strokeStyle = grad
    ctx.stroke()
  }

  ctx.restore()
}

export function GradientRibbonCanvas({ className }: GradientRibbonCanvasProps) {
  const { canvasRef, containerRef } = useCanvasAnimation({
    onFrame: (ctx, meta) => {
      drawRibbons(ctx, meta, performance.now())
    },
  })

  return (
    <div ref={containerRef} className={className} style={{ width: '100%', height: '100%' }}>
      <canvas ref={canvasRef} className="block" aria-hidden="true" />
    </div>
  )
}
